import { configPublic } from '@src/config.public'
import { isVideoCompleted } from '@src/mappers/media/video-history.helpers'

export type TrackableContent =
  | Pick<CocoApi.CommonProperties, 'tracks' | 'trackList' | 'entities' | 'userData'>
  | null
  | undefined

const isTrackEntity = (e: unknown): e is CocoApi.TrackEntity =>
  !!e && typeof e === 'object' && (e as { _type?: string })._type === 'track'

const normalizeLegacyTrack = (track: CocoApi.TrackListItem): CocoApi.TrackEntity => ({
  ...track,
  _id: track._id ?? track.id,
  _type: 'track',
  segments: track.segments ?? [],
})

export const getTracks = (content: TrackableContent): CocoApi.TrackEntity[] => {
  const entities = content?.entities
  if (content?.tracks?.length && entities) {
    return content.tracks.map((id) => entities[id]).filter(isTrackEntity)
  }

  if (content?.trackList) {
    return content.trackList.map(normalizeLegacyTrack)
  }

  return Object.values(content?.entities ?? {}).filter(isTrackEntity)
}

export const hasTracks = (content: TrackableContent): boolean =>
  configPublic.featureFlags.isTrackEnabled && getTracks(content).length > 0

export const hasLegacyProgress = (content: TrackableContent): boolean =>
  Array.isArray(content?.trackList) && content.trackList.length > 0

// Unlike hasTracks, false for the stub trackList COCOA serves users still on
// legacy program progress.
export const usesTrackProgress = (content: TrackableContent): boolean =>
  hasTracks(content) && !hasLegacyProgress(content)

export const getEnrolledTrack = (content: TrackableContent): CocoApi.TrackEntity | null =>
  getTracks(content).find((t) => t.userData?.isEnrolled) ?? null

export const getDefaultTrack = (content: TrackableContent): CocoApi.TrackEntity | null => {
  const tracks = getTracks(content)
  return tracks.find((t) => t.isDefault) ?? tracks[0] ?? null
}

// COCOA marks track enrollments on the track entity's userData, not the program's.
export const isContentEnrolled = (content: TrackableContent): boolean =>
  !!content?.userData?.isEnrolled || !!getEnrolledTrack(content)

/** `one-required` is an OR choice with no client UI, so those days are hidden. */
export const isSupportedTrackDay = (day: CocoApi.DayV2): boolean => day.strategy !== 'one-required'

export const isTrackDayItemRequired = (strategy: CocoApi.DayV2Strategy, index: number): boolean => {
  switch (strategy) {
    case 'all-required':
      return true
    case 'first-required':
      return index === 0
    case 'none-required':
    case 'one-required':
      return false
  }
}

const getTrackDays = (track: CocoApi.TrackEntity | null | undefined): CocoApi.DayV2[] =>
  (track?.segments ?? []).flatMap((segment) => (segment.days ?? []).filter(isSupportedTrackDay))

export const getTrackDayIds = (track: CocoApi.TrackEntity | null | undefined): string[] =>
  (track?.segments ?? []).flatMap((segment) => (segment.days ?? []).map((day) => day._id))

/** A workout can repeat across days; each occurrence progresses independently. */
export interface TrackItemRef {
  entityId: string
  dayId: string
}

/** Key for an occurrence in a completion map. Never key those maps by entity id. */
export const getTrackItemKey = (dayId: string, entityId: string): string => `${dayId}:${entityId}`

export const getTrackItemRefKey = (ref: TrackItemRef): string =>
  getTrackItemKey(ref.dayId, ref.entityId)

const getTrackItemRefs = (
  track: CocoApi.TrackEntity | null | undefined,
  options: { requiredOnly?: boolean } = {}
): TrackItemRef[] =>
  getTrackDays(track).flatMap((day) =>
    (day.items ?? [])
      .filter((_, index) => !options.requiredOnly || isTrackDayItemRequired(day.strategy, index))
      .map((item) => ({ entityId: item._id, dayId: day._id }))
  )

export const getTrackOrderedItems = (track: CocoApi.TrackEntity | null): TrackItemRef[] =>
  getTrackItemRefs(track)

export const getTrackRequiredItems = (track: CocoApi.TrackEntity | null): TrackItemRef[] =>
  getTrackItemRefs(track, { requiredOnly: true })

export const getTrackProgressCounts = (
  track: CocoApi.TrackEntity | null,
  completionState: Record<string, boolean>
): { completed: number; total: number } => {
  const requiredItems = getTrackRequiredItems(track)
  const completed = requiredItems.filter((ref) => completionState[getTrackItemRefKey(ref)]).length
  return { completed, total: requiredItems.length }
}

// completedDayIds is authoritative for a day's required items only; the client
// can be missing history the API already counted.
export const getTrackCompletionState = (
  track: CocoApi.TrackEntity | null,
  entities: CocoApi.Entities | undefined,
  completedDayIds?: string[] | null
): Record<string, boolean> => {
  const completedDays = new Set(completedDayIds ?? [])
  const state: Record<string, boolean> = {}

  for (const day of getTrackDays(track)) {
    const isDayCompleted = completedDays.has(day._id)

    for (const [index, item] of (day.items ?? []).entries()) {
      const playable = entities?.[item._id] as CocoApi.PlayableEntity | undefined
      if (!playable) continue

      const isRequired = isTrackDayItemRequired(day.strategy, index)
      state[getTrackItemKey(day._id, item._id)] =
        (isDayCompleted && isRequired) || isVideoCompleted(playable, day._id)
    }
  }
  return state
}

// Not badge status: a badge only goes active on enrollment, so the first
// enrollment would reset workouts watched before it.
export const hasCompletedTrackProgress = (
  track: CocoApi.TrackEntity | null,
  entities: CocoApi.Entities | undefined,
  completedDayIds?: string[] | null
): boolean => Object.values(getTrackCompletionState(track, entities, completedDayIds)).some(Boolean)

// Prefer apiNextItem when it names a required occurrence; the API sees history
// the client may not.
export const getTrackNextItem = (
  track: CocoApi.TrackEntity | null,
  completionState: Record<string, boolean>,
  apiNextItem?: TrackItemRef | null
): TrackItemRef | null => {
  const requiredItems = getTrackRequiredItems(track)

  if (
    apiNextItem &&
    !completionState[getTrackItemKey(apiNextItem.dayId, apiNextItem.entityId)] &&
    requiredItems.some(
      (ref) => ref.dayId === apiNextItem.dayId && ref.entityId === apiNextItem.entityId
    )
  ) {
    return apiNextItem
  }

  return requiredItems.find((ref) => !completionState[getTrackItemRefKey(ref)]) ?? null
}

const isBadgeEntity = (e: unknown): e is CocoApi.BadgeEntity =>
  !!e && typeof e === 'object' && (e as { _type?: string })._type === 'badge'

export const getTrackBadgeEntity = (
  track: CocoApi.TrackEntity | null | undefined,
  entities: CocoApi.Entities | undefined
): CocoApi.BadgeEntity | null => {
  const badgeId = track?.badgeId
  if (!badgeId) return null

  const entity = entities?.[badgeId]
  return isBadgeEntity(entity) ? entity : null
}

// COCOA sends the track only entity ids; first match wins.
export const getTrackAssociatedFileUrl = (
  track: CocoApi.TrackEntity | null | undefined,
  entities: CocoApi.Entities | undefined
): string | null => {
  const associated = track?.associatedContent

  for (const id of associated?.entityIds ?? []) {
    const entity = (associated?.entities?.[id] ?? entities?.[id]) as
      CocoApi.ResourceEntity | undefined
    if (entity?.file?.url) return entity.file.url
  }

  return null
}

export type CommitMode = 'program' | 'singleTrack' | 'multiTrack'

export const getCommitMode = (content: TrackableContent): CommitMode => {
  if (!hasTracks(content)) return 'program'
  return getTracks(content).length === 1 ? 'singleTrack' : 'multiTrack'
}
